"use client";

import { KeyRound, Pencil, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/app/_module/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/_module/components/ui/card";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ResponsiveDialogContent,
} from "@/app/_module/components/ui/dialog";
import { Input } from "@/app/_module/components/ui/input";
import { Label } from "@/app/_module/components/ui/label";
import { useToast } from "@/app/_module/hooks/use-toast";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { getSupabaseClient } from "@/app/_module/services/supabaseClient";
import { passkeyCancelled } from "@/app/_module/services/supabaseAuthService";
import { isDemoMode } from "@/app/_module/utils/demoMode";
import { isNativePlatform } from "@/lib/capacitor/platform";

type Row = { id: string; name: string; createdAt: string; lastUsedAt?: string; confirmed: boolean };

const errorText = (e: any, fallback: string) => e?.response?.data?.message || e?.message || fallback;

const addErrorText = (error: unknown) => {
  const code = (error as any)?.code || (error as any)?.error_code;
  if (code === "ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED") return "This device already has a passkey for your account.";
  if (code === "ERROR_INVALID_RP_ID" || code === "ERROR_INVALID_DOMAIN") return "Passkeys don't work on this web address.";
  if (code === "passkey_disabled") return "Passkeys aren't available yet.";
  return "Couldn't add the passkey. Try again.";
};

/** "Chrome on Mac": a starting name when the browser doesn't give one. Renamable in the list. */
const deviceName = () => {
  const ua = navigator.userAgent;
  const os = /iPhone/.test(ua) ? "iPhone" : /iPad/.test(ua) ? "iPad" : /Android/.test(ua) ? "Android"
    : /Mac OS X/.test(ua) ? "Mac" : /Windows/.test(ua) ? "Windows" : /CrOS/.test(ua) ? "Chromebook" : "this device";
  const browser = /Edg\//.test(ua) ? "Edge" : /Firefox\//.test(ua) ? "Firefox" : /Chrome\//.test(ua) ? "Chrome"
    : /Safari\//.test(ua) ? "Safari" : "Browser";
  return `${browser} on ${os}`;
};

const day = (iso?: string) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "";

/**
 * Settings → Security → Passkeys. Web only: the native app's WebView can't reach the device's
 * passkeys. Adding one is password → browser prompt → /auth/passkeys/confirm; only confirmed
 * passkeys skip the 2-step code at sign-in, so any passkey on the account that wasn't added here
 * is flagged for removal.
 */
export const PasskeyManagement = function () {
  const { toast } = useToast();
  const [supported, setSupported] = useState(false);
  const [rows, setRows] = useState<Row[] | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [adding, setAdding] = useState(false);
  const [password, setPassword] = useState("");
  const [step, setStep] = useState<"idle" | "password" | "browser">("idle");
  const [addError, setAddError] = useState("");
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const [removing, setRemoving] = useState<Row | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const supabase = await getSupabaseClient();
    const [{ data, error }, confirmedRes] = await Promise.all([
      supabase.auth.passkey.list(),
      api.get({ url: getApiUrl(endpoint.auth.passkeysConfirmed), auth: true }).catch(() => null),
    ]);
    if (error) {
      if (((error as any).code || (error as any).error_code) === "passkey_disabled") setUnavailable(true);
      else setRows([]);
      return;
    }
    // If the confirmed list didn't load, flag nothing rather than everything.
    const confirmedIds: string[] | null = (confirmedRes as any)?.data?.confirmedIds ?? null;
    setRows(
      (data ?? []).map((p) => ({
        id: p.id,
        name: p.friendly_name || "Passkey",
        createdAt: p.created_at,
        lastUsedAt: p.last_used_at,
        confirmed: confirmedIds ? confirmedIds.includes(p.id) : true,
      })),
    );
  };

  useEffect(() => {
    if (isNativePlatform() || isDemoMode() || typeof window === "undefined" || !window.PublicKeyCredential) return;
    setSupported(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!supported || unavailable) return null;

  const closeAdd = () => {
    if (step !== "idle") return;
    setAdding(false);
    setPassword("");
    setAddError("");
  };

  const addPasskey = async () => {
    setAddError("");
    setStep("password");
    try {
      await api.post({ url: getApiUrl(endpoint.auth.passkeyCheckPassword), body: { password }, auth: true });
    } catch (e) {
      setAddError(errorText(e, "Couldn't check your password. Try again."));
      setStep("idle");
      return;
    }
    setStep("browser");
    const supabase = await getSupabaseClient();
    const { data, error } = await supabase.auth.registerPasskey();
    if (error || !data) {
      setAddError(passkeyCancelled(error) ? "The browser prompt was closed. Select Continue to try again." : addErrorText(error));
      setStep("idle");
      return;
    }
    if (!data.friendly_name) {
      await supabase.auth.passkey.update({ passkeyId: data.id, friendlyName: deviceName() }).catch(() => undefined);
    }
    let confirmed = true;
    try {
      await api.post({ url: getApiUrl(endpoint.auth.passkeyConfirm), body: { passkeyId: data.id }, auth: true });
    } catch (e) {
      confirmed = false;
      toast({
        title: "Passkey added but not confirmed",
        description: `${errorText(e, "We couldn't confirm it.")} Until then, passkey sign-in asks for your 2-step code.`,
        variant: "destructive",
      });
    }
    setStep("idle");
    setAdding(false);
    setPassword("");
    await load();
    if (confirmed) toast({ title: "Passkey added", description: "Next time, choose it on the sign-in page instead of typing your password." });
  };

  const saveName = async () => {
    if (!editing) return;
    const name = editing.name.trim().slice(0, 120);
    if (!name) return setEditing(null);
    setBusy(true);
    const supabase = await getSupabaseClient();
    const { error } = await supabase.auth.passkey.update({ passkeyId: editing.id, friendlyName: name });
    setBusy(false);
    if (error) return toast({ title: "Couldn't rename the passkey", description: "Try again.", variant: "destructive" });
    setEditing(null);
    await load();
  };

  const remove = async () => {
    if (!removing) return;
    setBusy(true);
    const supabase = await getSupabaseClient();
    const { error } = await supabase.auth.passkey.delete({ passkeyId: removing.id });
    setBusy(false);
    if (error) return toast({ title: "Couldn't remove the passkey", description: "Try again.", variant: "destructive" });
    setRemoving(null);
    await load();
    toast({ title: "Passkey removed" });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <KeyRound className="h-5 w-5" />
          <CardTitle>Passkeys</CardTitle>
        </div>
        <CardDescription>
          Sign in with your fingerprint, face or device PIN instead of your password. A passkey added here also
          covers your 2-step code.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {rows === null ? (
          <p className="text-sm text-muted-foreground">Loading your passkeys…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No passkeys yet.</p>
        ) : (
          <ul className="divide-y rounded-lg border">
            {rows.map((row) => (
              <li key={row.id} className="flex items-start justify-between gap-3 p-3">
                <div className="min-w-0 flex-1">
                  {editing?.id === row.id ? (
                    <form
                      className="flex items-center gap-2"
                      onSubmit={(e) => {
                        e.preventDefault();
                        saveName();
                      }}
                    >
                      <Input
                        aria-label="Passkey name"
                        autoFocus
                        maxLength={120}
                        value={editing.name}
                        onChange={(e) => setEditing({ id: row.id, name: e.target.value })}
                        onKeyDown={(e) => e.key === "Escape" && setEditing(null)}
                        disabled={busy}
                      />
                      <Button type="submit" size="sm" disabled={busy}>Save</Button>
                      <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(null)} disabled={busy}>
                        Cancel
                      </Button>
                    </form>
                  ) : (
                    <p className="truncate font-medium">{row.name}</p>
                  )}
                  <p className="text-sm text-muted-foreground">
                    Added {day(row.createdAt)} · {row.lastUsedAt ? `Last used ${day(row.lastUsedAt)}` : "Not used yet"}
                  </p>
                  {!row.confirmed && (
                    <p className="mt-1 text-sm text-amber-700">
                      Not added from this page. If you don't recognize it, remove it. While it's on your account,
                      passkey sign-in asks for your 2-step code.
                    </p>
                  )}
                </div>
                {editing?.id !== row.id && (
                  <div className="flex shrink-0 items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Rename ${row.name}`}
                      onClick={() => setEditing({ id: row.id, name: row.name })}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" aria-label={`Remove ${row.name}`} onClick={() => setRemoving(row)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        <Button onClick={() => setAdding(true)} className="w-full sm:w-auto">
          Add a passkey
        </Button>
      </CardContent>

      <Dialog open={adding} onOpenChange={(open) => (open ? setAdding(true) : closeAdd())}>
        <ResponsiveDialogContent size="md">
          <DialogHeader>
            <DialogTitle>Add a passkey</DialogTitle>
            <DialogDescription>
              Enter your password. Your browser then asks for your fingerprint, face or device PIN to save the passkey.
            </DialogDescription>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (password && step === "idle") addPasskey();
            }}
          >
            <div className="space-y-2">
              <Label htmlFor="passkey-password">Password</Label>
              <Input
                id="passkey-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={step !== "idle"}
              />
              {!!addError && <p className="text-sm text-red-600">{addError}</p>}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeAdd} disabled={step !== "idle"}>
                Cancel
              </Button>
              <Button type="submit" disabled={!password || step !== "idle"}>
                {step === "password" ? "Checking…" : step === "browser" ? "Waiting for your browser…" : "Continue"}
              </Button>
            </DialogFooter>
          </form>
        </ResponsiveDialogContent>
      </Dialog>

      <Dialog open={!!removing} onOpenChange={(open) => !open && !busy && setRemoving(null)}>
        <ResponsiveDialogContent size="md">
          <DialogHeader>
            <DialogTitle>Remove this passkey?</DialogTitle>
            <DialogDescription>
              You won't be able to sign in with &ldquo;{removing?.name}&rdquo; anymore.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoving(null)} disabled={busy}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={remove} disabled={busy}>
              Remove
            </Button>
          </DialogFooter>
        </ResponsiveDialogContent>
      </Dialog>
    </Card>
  );
};
